import React from 'react';
import { Routes, Route, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './App.css';
import Header from './components/Header';
import Hero from './components/Hero';
import AppFlow from './components/AppFlow';
import Features from './components/Features';
import BusinessValue from './components/BusinessValue';
import Pricing from './components/Pricing';
import FAQ from './components/FAQ';
import Contact from './components/Contact';
import Footer from './components/Footer';
import LegalPage from './components/LegalPage';
import SEOHead from './components/SEOHead';
import StructuredData from './components/StructuredData';
import FAQSchema from './components/FAQSchema';
import GoogleAnalyticsWhenConsented from './components/GoogleAnalyticsWhenConsented';
import CookieConsentBanner, { getConsent } from './components/CookieConsentBanner';
import ShareRedirect from './components/ShareRedirect';
import CheckoutPage from './components/CheckoutPage';
import PaymentResultPage from './components/PaymentResultPage';

function MarketingLayout() {
  return (
    <>
      <Header />
      <Outlet />
      <Footer />
    </>
  );
}

function HomePage() {
  return (
    <main>
      <Hero />
      <AppFlow />
      <Features />
      <BusinessValue />
      <Pricing />
      <FAQ />
      <Contact />
    </main>
  );
}

function App() {
  const { i18n } = useTranslation();
  const location = useLocation();
  const [consent, setConsent] = React.useState(() => getConsent());
  const embeddedCheckout =
    location.pathname === '/checkout' &&
    new URLSearchParams(location.search).get('embed') === '1';

  React.useEffect(() => {
    const handleShowCookieConsent = () => setConsent(null);
    window.addEventListener('glowapp-show-cookie-consent', handleShowCookieConsent);
    return () => window.removeEventListener('glowapp-show-cookie-consent', handleShowCookieConsent);
  }, []);

  React.useEffect(() => {
    const currentLang = i18n.language || 'en';
    const langCode = currentLang.split('-')[0];
    const validLangs = ['en', 'he', 'ar', 'ru'];
    const finalLang = validLangs.includes(langCode) ? langCode : 'en';

    if (i18n.language !== finalLang) {
      i18n.changeLanguage(finalLang);
    }

    const dir = finalLang === 'ar' || finalLang === 'he' ? 'rtl' : 'ltr';
    document.documentElement.dir = dir;
    document.documentElement.lang = finalLang;
  }, [i18n.language]);

  return (
    <div className={embeddedCheckout ? 'App is-embed-checkout' : 'App'}>
      {!embeddedCheckout && consent === 'granted' && <GoogleAnalyticsWhenConsented />}
      {!embeddedCheckout && consent === null && (
        <CookieConsentBanner
          onAccept={() => setConsent('granted')}
          onReject={() => setConsent('denied')}
        />
      )}
      {!embeddedCheckout && <SEOHead />}
      {!embeddedCheckout && <StructuredData />}
      {!embeddedCheckout && <FAQSchema />}
      <Routes>
        <Route path="/s/:code" element={<ShareRedirect />} />
        <Route path="/b/:businessId" element={<ShareRedirect />} />
        <Route path="/e/:businessId/:employeeId" element={<ShareRedirect />} />
        <Route path="/checkout" element={<CheckoutPage />} />
        <Route path="/payment/success" element={<PaymentResultPage kind="success" />} />
        <Route path="/payment/cancel" element={<PaymentResultPage kind="cancel" />} />
        <Route path="/payment/portal-return" element={<PaymentResultPage kind="portal" />} />
        <Route element={<MarketingLayout />}>
          <Route path="/" element={<HomePage />} />
          <Route
            path="/help"
            element={
              <LegalPage
                titleKey="help"
                contentKey="helpPageContent"
              />
            }
          />
          <Route
            path="/privacy"
            element={
              <LegalPage
                titleKey="privacyPolicy"
                contentKey="privacyPolicyContent"
              />
            }
          />
          <Route
            path="/terms"
            element={
              <LegalPage
                titleKey="termsOfService"
                contentKey="termsOfUseContent"
              />
            }
          />
        </Route>
      </Routes>
    </div>
  );
}

export default App;
